import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getOverviewApi } from "../../../api/request/clients/clients";
import SkeletonTable from "../../../components/table/SkeletonTable";
import {
  documentColumn,
  fileColumns,
  signatureColumn,
  invoiceColumns,
  recentActivityColumn,
} from "../../../columns/clients/clientDetails/overview";
import { Progress, Spin } from "antd";
import View from "../../../assets/View";
import GlobalTooltip from "../../../components/common/GlobalTooltip";
import ViewDocModal from "../../files/components/ViewDocModal";
import { useSelector } from "react-redux";

const Overview = ({ changeTab }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [documentData, setDocumentData] = useState([]);
  const [fileData, setFileData] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);
  const [signatureData, setSignatureData] = useState([]);
  const [recentActivityData, setRecentActivityData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocOpen, setIsDocOpen] = useState(false);
  const [selectedFileForAckDocs, setSelectedFileForAckDocs] = useState(null);
  const userDetails = useSelector((state) => state?.user?.userDetails);

  const getOverviewData = async () => {
    try {
      setIsLoading(true);
      const { data } = await getOverviewApi({ id: id });
      if (data?.status) {
        setDocumentData(data?.data?.recent_documents);
        setFileData(
          data?.data?.files?.map((item) => {
            return {
              ...item,
              task: item?.running_task?.pipeline_manage_task,
              ...(item?.acknowledge?.length && {
                acknowledgement_doc: (
                  <a
                    className="text-blue-500 cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedFileForAckDocs(item);
                      setIsDocOpen(true);
                    }}
                  >
                    View
                  </a>
                ),
              }),

              progress: (
                <div className="min-w-[170px] w-auto">
                  <Progress
                    percent={Number(item?.completion_percentage).toFixed(0)}
                  />
                </div>
              ),
            };
          })
        );
        setInvoiceData(
          data?.data?.latest_invoice?.map((item) => {
            return {
              ...item,
              fileName: item?.files?.service_name_slug,
              fileNumber: item?.files?.file_number,
              action: (
                <GlobalTooltip title={"View Invoice"}>
                  <button
                    onClick={() =>
                      window.open(item?.files?.invoice_pdf?.url, "_blank")
                    }
                  >
                    <View />
                  </button>
                </GlobalTooltip>
              ),
            };
          })
        );
        setSignatureData(
          data?.data?.signature?.map((item) => ({
            ...item,
            fileName: item?.file?.service_name_slug,
            fileNumber: item?.file?.file_number,
          }))
        );
        setRecentActivityData(
          data?.data?.recent_activity?.map((item) => {
            return {
              ...item,
              activity: (
                <div
                  className="w-[150px] overflow-hidden whitespace-nowrap text-ellipsis"
                  title={item?.activity}
                >
                  {item?.activity}
                </div>
              ),
              message: (
                <div
                  className="w-[150px] overflow-hidden whitespace-nowrap text-ellipsis"
                  title={item?.activity_message}
                >
                  {item?.activity_message}
                </div>
              ),
            };
          })
        );
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigate = (data) => {
    if(userDetails?.role !== "admin") return;
    if (data?.status === "closed") {
      navigate(`/files_report/${data?.service_type_id}`, {
        state: { status: "closed", service: data?.service_type?.service_name },
      });
    } else if (data?.status === "inprocess") {
      navigate(`/file/${data?.service_type_id}/${data?.id}`, {
        state: { client: data?.user, task: data?.task },
      });
    } else if (data?.status === "unsubmitted") {
      navigate(`/files_report/${data?.service_type_id}`, {
        state: {
          status: "unsubmitted",
          service: data?.service_type?.service_name,
        },
      });
    }
  };

  useEffect(() => {
    if (id) getOverviewData();
  }, [id]);

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center h-[200px]">
          <Spin />
        </div>
      ) : (
        <div className="m-5 space-y-5">
          <div className="border rounded-xl overflow-hidden">
            <div className="flex justify-between mx-5 mt-5">
              <p className="title">Files</p>
              <p
                className="cursor-pointer text-primary"
                onClick={() => changeTab({ id: "4", title: "Files" })}
              >
                View All
              </p>
            </div>

            <div className="mt-5">
              <SkeletonTable
                columns={fileColumns}
                data={fileData}
                onRowClick={(data) => handleNavigate(data)}
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 grid-cols-1 gap-5">
            <div className="border rounded-xl overflow-hidden">
              <div className="flex justify-between mx-5 mt-5">
                <p className="title">Documents</p>
                <p
                  className="cursor-pointer text-primary"
                  onClick={() => changeTab({ id: "3", title: "Documents" })}
                >
                  View All
                </p>
              </div>

              <div className="mt-5">
                <SkeletonTable
                  columns={documentColumn}
                  data={documentData}
                  onRowClick={(data)=> userDetails?.role === "admin" ? window.open(data?.path, "_blank") : null}
                />
              </div>
            </div>
            <div className="border rounded-xl overflow-hidden">
              <div className="flex justify-between mx-5 mt-5">
                <p className="title">Recent Activity</p>
                <p
                  className="cursor-pointer text-primary"
                  onClick={() => changeTab({ id: "8", title: "Activity" })}
                >
                  View All
                </p>
              </div>

              <div className="mt-5">
                <SkeletonTable
                  columns={recentActivityColumn}
                  data={recentActivityData}
                />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 grid-cols-1 gap-5">
            <div className="border rounded-xl overflow-hidden">
              <div className="flex justify-between mx-5 mt-5">
                <p className="title">Invoices</p>
                <p
                  className="cursor-pointer text-primary"
                  onClick={() => changeTab({ id: "5", title: "Invoice" })}
                >
                  View All
                </p>
              </div>

              <div className="mt-5">
                <SkeletonTable columns={invoiceColumns} data={invoiceData} />
              </div>
            </div>

            <div className="border rounded-xl overflow-hidden">
              <div className="flex justify-between mx-5 mt-5">
                <p className="title">Signatures</p>
                <p
                  className="cursor-pointer text-primary"
                  onClick={() => changeTab({ id: "7", title: "Signature" })}
                >
                  View All
                </p>
              </div>
              <div className="mt-5">
                <SkeletonTable
                  columns={signatureColumn}
                  data={signatureData}
                  onRowClick={(data) => userDetails?.role === "admin" ? window.open(data?.path, "_blank") : null}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ViewDocModal
        isDocOpen={isDocOpen}
        setIsDocOpen={setIsDocOpen}
        selectedFileForAckDocs={selectedFileForAckDocs}
      />
    </>
  );
};

export default Overview;
